import { useEffect, useMemo, useState, type ReactNode } from "react";
import { FiChevronRight, FiEye, FiEyeOff, FiLock, FiMail, FiRefreshCw, FiTrash2, FiUpload } from "react-icons/fi";
import {
  DomainSidebar,
  DomainSidebarHeader,
  DomainTwoColumnContent,
  DomainTwoColumnLayout,
} from "@/components/ui/DomainSidebar";
import { ModalShell } from "@/components/ui/ModalShell";
import { AppButton, FormField, MessageBanner, ModalActions, TextInput } from "@/components/ui/primitives";
import { useAuth } from "@/hooks/useAuth";
import { usePreferencesStore } from "@/stores/usePreferencesStore";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/theme";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";
import { TransactionCsvImportModal } from "../components/TransactionCsvImportModal";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { Link } from "@tanstack/react-router";
import { confirmAction } from "@/components/ui/confirmDialog";
import { McpTokensPanel } from "../components/McpTokensPanel";
import { McpGrantsPanel } from "../components/McpGrantsPanel";

type ProfileFormState = {
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
};

type PasswordFormState = {
  oldPassword: string;
  newPassword: string;
};

const normalizeFormState = (user: {
  email?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  phone?: string | null;
} | null): ProfileFormState => ({
  email: user?.email ?? "",
  firstname: user?.firstname ?? "",
  lastname: user?.lastname ?? "",
  phone: user?.phone ?? "",
});

const buildInitials = (firstname?: string | null, lastname?: string | null, email?: string | null) => {
  const joined = [firstname, lastname]
    .filter((value): value is string => Boolean(value?.trim()))
    .map((value) => value.trim()[0]?.toUpperCase())
    .join("");

  if (joined) {
    return joined.slice(0, 2);
  }

  const fallback = email?.trim()?.[0]?.toUpperCase();

  return fallback || "U";
};

type EditableFieldProps = {
  id: string;
  label: string;
  name: keyof ProfileFormState;
  type?: "email" | "text" | "tel";
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

const EditableField = ({
  id,
  label,
  name,
  type = "text",
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
}: EditableFieldProps) => (
  // A plain FormField, like every other form in the app: the field used to sit inside its
  // own tinted card, which made a text input read as a heavier element than the panel
  // holding it.
  <FormField label={label} htmlFor={id} className={className}>
    <TextInput
      id={id}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
    />
  </FormField>
);

type PreferenceChoice = {
  id: string;
  label: string;
  description?: string;
  ariaLabel?: string;
};

type SettingsSectionProps = {
  id: string;
  title: string;
  description?: string;
  children: ReactNode;
};

const SettingsSection = ({ id, title, description, children }: SettingsSectionProps) => (
  <section id={id} aria-labelledby={`${id}-title`} className="app-panel-raised scroll-mt-24 p-5 sm:p-6">
    <header className="mb-5">
      <h2 id={`${id}-title`} className="text-xl font-medium tracking-[-0.02em] text-[var(--app-text)]">
        {title}
      </h2>
      {description ? <p className="mt-1 text-sm leading-5 text-[var(--app-text-secondary)]">{description}</p> : null}
    </header>
    {children}
  </section>
);

type PreferenceGroupProps = {
  label: string;
  ariaLabel: string;
  name: string;
  options: PreferenceChoice[];
  activeId: string;
  onSelect: (id: string) => void;
};

const PreferenceGroup = ({
  label,
  ariaLabel,
  name,
  options,
  activeId,
  onSelect,
}: PreferenceGroupProps) => (
  <section>
    <p className="app-domain-sidebar-label">{label}</p>
    {/* La misma estructura que la navbar: un track que aclara sobre el panel y, dentro, la
        opción activa oscureciendo con el par de superficies de SegmentedControl. */}
    <div className="app-panel-raised-track mt-3 grid gap-2 p-2" role="radiogroup" aria-label={ariaLabel}>
      {options.map((option) => {
        const isActive = option.id === activeId;

        return (
          <label
            key={option.id}
            className={`flex items-center justify-between rounded-[var(--ds-radius-md)] px-3 py-3 text-left text-sm transition focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--app-focus-ring)] ${
              isActive
                ? "bg-[var(--app-segmented-active-bg)] text-[var(--app-text-secondary)]"
                : "text-[var(--app-text-secondary)] hover:bg-[var(--app-segmented-hover-bg)]"
            }`}
          >
            <input
              type="radio"
              name={name}
              value={option.id}
              checked={isActive}
              onChange={() => onSelect(option.id)}
              className="sr-only"
              aria-label={option.ariaLabel ?? option.label}
            />
            <span>
              <span className="block font-medium">{option.label}</span>
              {option.description ? (
                <span className="mt-1 block text-xs leading-5 text-[var(--app-text-secondary)]">
                  {option.description}
                </span>
              ) : null}
            </span>
            <span
              aria-hidden="true"
              className={`h-2 w-2 rounded-full ${
                isActive ? "bg-[var(--app-primary)]" : "bg-[var(--app-border)]"
              }`}
            />
          </label>
        );
      })}
    </div>
  </section>
);

type ChangePasswordModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (state: PasswordFormState) => Promise<boolean>;
};

const emptyPasswordForm: PasswordFormState = {
  oldPassword: "",
  newPassword: "",
};

type PasswordInputProps = {
  id: string;
  name: keyof PasswordFormState;
  value: string;
  autoComplete: string;
  disabled: boolean;
  isVisible: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onToggleVisibility: () => void;
};

const PasswordInput = ({
  id,
  name,
  value,
  autoComplete,
  disabled,
  isVisible,
  onChange,
  onToggleVisibility,
}: PasswordInputProps) => {
  const { t } = useTranslation(["profile"]);

  return (
    <div className="relative">
      <TextInput
        id={id}
        name={name}
        type={isVisible ? "text" : "password"}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="pr-12"
      />
      <button
        type="button"
        onClick={onToggleVisibility}
        disabled={disabled}
        className="absolute right-1 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-xl text-[var(--app-muted-text)] transition hover:bg-[var(--app-surface-hover)] hover:text-[var(--app-text)] disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={isVisible ? t("profile.passwordModal.hidePassword") : t("profile.passwordModal.showPassword")}
        aria-pressed={isVisible}
      >
        {isVisible ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
      </button>
    </div>
  );
};

const ChangePasswordModal = ({ isOpen, onClose, onSubmit }: ChangePasswordModalProps) => {
  const { t } = useTranslation(["profile", "common"]);
  const [formState, setFormState] = useState<PasswordFormState>(emptyPasswordForm);
  const [visibleFields, setVisibleFields] = useState<Record<keyof PasswordFormState, boolean>>({
    oldPassword: false,
    newPassword: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setFormState(emptyPasswordForm);
      setVisibleFields({ oldPassword: false, newPassword: false });
      setFeedback(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setFeedback(null);
    setFormState((current) => ({ ...current, [name]: value }));
  };

  const togglePasswordVisibility = (field: keyof PasswordFormState) => {
    setVisibleFields((current) => ({ ...current, [field]: !current[field] }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting || !formState.oldPassword || !formState.newPassword) {
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    const changed = await onSubmit(formState);

    if (changed) {
      setFeedback({ type: "success", message: t("profile.messages.passwordSuccess") });
      setFormState(emptyPasswordForm);
    } else {
      setFeedback({ type: "error", message: t("profile.messages.passwordError") });
    }

    setIsSubmitting(false);
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={t("profile.passwordModal.title")}
      subtitle={t("profile.passwordModal.subtitle")}
      labelledBy="change-password-modal-title"
      maxWidthClassName="max-w-[34rem]"
      panelClassName="!rounded-[28px]"
      headerClassName="!px-6 !pt-6 !pb-4 sm:!px-7 sm:!pt-7 sm:!pb-5 [&_h2]:!mb-2 [&_h2]:!text-[1.75rem] [&_h2]:!tracking-[-0.03em] sm:[&_h2]:!text-[2rem] [&_p]:!max-w-md [&_p]:!text-sm [&_p]:!leading-5"
      contentClassName="!px-6 !pb-6 sm:!px-7 sm:!pb-7"
    >
      <form className="space-y-4 pt-1" onSubmit={handleSubmit}>
        {feedback ? (
          <MessageBanner tone={feedback.type === "success" ? "success" : "error"}>
            {feedback.message}
          </MessageBanner>
        ) : null}

        <FormField label={t("profile.passwordModal.oldPassword")} htmlFor="old-password" required>
          <PasswordInput
            id="old-password"
            name="oldPassword"
            autoComplete="current-password"
            value={formState.oldPassword}
            onChange={handleInputChange}
            disabled={isSubmitting}
            isVisible={visibleFields.oldPassword}
            onToggleVisibility={() => togglePasswordVisibility("oldPassword")}
          />
        </FormField>

        <FormField label={t("profile.passwordModal.newPassword")} htmlFor="new-password" required>
          <PasswordInput
            id="new-password"
            name="newPassword"
            autoComplete="new-password"
            value={formState.newPassword}
            onChange={handleInputChange}
            disabled={isSubmitting}
            isVisible={visibleFields.newPassword}
            onToggleVisibility={() => togglePasswordVisibility("newPassword")}
          />
        </FormField>

        <ModalActions
          cancelLabel={t("common.actions.cancel")}
          submitLabel={t("profile.passwordModal.submit")}
          submittingLabel={t("common.states.saving")}
          onCancel={onClose}
          isSubmitting={isSubmitting}
          isSubmitDisabled={!formState.oldPassword || !formState.newPassword}
        />
      </form>
    </ModalShell>
  );
};

export const ProfileView = () => {
  const { t } = useTranslation(["profile", "common"]);
  const { user, fetchUserProfile, updateUserProfile, changePassword, deleteCurrentUser } = useAuth();
  const language = usePreferencesStore((state) => state.language);
  const saveLanguage = usePreferencesStore((state) => state.saveLanguage);
  const appMode = usePreferencesStore((state) => state.appMode);
  const saveAppMode = usePreferencesStore((state) => state.saveAppMode);
  const { setMode, mode } = useTheme();
  const [formState, setFormState] = useState<ProfileFormState>(() => normalizeFormState(user));
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isCsvImportModalOpen, setIsCsvImportModalOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);
  const [profileFeedback, setProfileFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    if (!user) {
      void fetchUserProfile();
    }
  }, [fetchUserProfile, user]);

  useEffect(() => {
    setFormState(normalizeFormState(user));
  }, [user]);

  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim() || user?.email || "User";
  const initials = buildInitials(user?.firstname, user?.lastname, user?.email);
  const isProfileDirty = useMemo(() => {
    const initialState = normalizeFormState(user);

    return (
      formState.email !== initialState.email ||
      formState.firstname !== initialState.firstname ||
      formState.lastname !== initialState.lastname ||
      formState.phone !== initialState.phone
    );
  }, [formState, user]);

  const themeOptions = [
    { id: "light", label: t("common.theme.light") },
    { id: "dark", label: t("common.theme.dark") },
    { id: "system", label: t("common.theme.system") },
  ];
  const languageOptions = [
    { id: "en", label: t("common.language.english") },
    { id: "es", label: t("common.language.spanish") },
  ];
  const appModeOptions = [
    {
      id: "simple",
      label: t("profile.appMode.simple.label"),
      description: t("profile.appMode.simple.description"),
      ariaLabel: t("profile.appMode.simple.ariaLabel"),
    },
    {
      id: "advanced",
      label: t("profile.appMode.advanced.label"),
      description: t("profile.appMode.advanced.description"),
      ariaLabel: t("profile.appMode.advanced.ariaLabel"),
    },
  ];
  const activeThemeId = themeOptions.some((option) => option.id === mode) ? mode : "system";
  const activeAppModeId = appModeOptions.some((option) => option.id === appMode) ? appMode : "simple";

  const handleProfileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setProfileFeedback(null);
    setFormState((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleProfileSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSavingProfile || !isProfileDirty) {
      return;
    }

    setIsSavingProfile(true);
    setProfileFeedback(null);

    const nextProfile = await updateUserProfile({
      email: formState.email.trim(),
      firstname: formState.firstname.trim() || null,
      lastname: formState.lastname.trim() || null,
      phone: formState.phone.trim() || null,
    });

    if (nextProfile) {
      setProfileFeedback({ type: "success", message: t("profile.messages.saveSuccess") });
    } else {
      setProfileFeedback({ type: "error", message: t("profile.messages.saveError") });
    }

    setIsSavingProfile(false);
  };

  const handlePasswordSubmit = async (state: PasswordFormState) => {
    return changePassword(state);
  };

  const handleDeleteAccount = async () => {
    if (isDeletingAccount) return;

    const confirmed = await confirmAction({
      title: t("profile.deleteAccount.confirmTitle"),
      body: t("profile.deleteAccount.confirmBody"),
      details: [
        t("profile.deleteAccount.anonymizationDetail"),
        t("profile.deleteAccount.sessionsDetail"),
        t("profile.deleteAccount.irreversibleDetail"),
      ],
      confirmLabel: t("profile.deleteAccount.confirm"),
      cancelLabel: t("common.actions.cancel", { defaultValue: "Cancel" }),
      tone: "danger",
    });
    if (!confirmed || isDeletingAccount) return;

    setIsDeletingAccount(true);
    setDeleteAccountError(null);
    const deleted = await deleteCurrentUser();
    if (!deleted) {
      setDeleteAccountError(t("profile.deleteAccount.error"));
      setIsDeletingAccount(false);
    }
  };

  return (
    <main className="app-page">
      <div className="app-container max-w-none overflow-x-hidden">
        <div className="relative mx-auto w-full min-w-0 max-w-2xl overflow-x-hidden md:max-w-none">
          <DomainTwoColumnLayout>
            <DomainSidebar>
            <DomainSidebarHeader
              greeting={t("common:labels.greeting")}
              title={t("common:nav.profile")}
              action={
                <AppButton
                  type="button"
                  tone="ghost"
                  size="sm"
                  onClick={() => {
                    void fetchUserProfile();
                  }}
                  leadingIcon={<FiRefreshCw className="h-4 w-4" />}
                >
                  {t("profile.refresh")}
                </AppButton>
              }
            />

            <section className="app-domain-sidebar-section">
              <p className="app-domain-sidebar-label">{t("profile.identity")}</p>
              <div className="flex items-center gap-3">
                <ProfileAvatar initials={initials} seed={user?.id || user?.email} size="menu" />
                <div className="min-w-0">
                  <p className="app-domain-sidebar-value">{fullName}</p>
                  <p className="app-domain-sidebar-meta flex min-w-0 items-center gap-2">
                    <FiMail className="h-4 w-4 shrink-0" />
                    <span className="truncate">{user?.email || t("common.empty.user")}</span>
                  </p>
                </div>
              </div>

              <p className="app-domain-sidebar-insight mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span
                  className={`inline-flex items-center gap-2 ${
                    user?.is_active ? "text-[var(--app-success)]" : "text-[var(--app-muted-text)]"
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      user?.is_active ? "bg-[var(--app-success)]" : "bg-[var(--app-border)]"
                    }`}
                  />
                  {user?.is_active ? t("profile.active") : t("profile.inactive")}
                </span>
                <span>{t("profile.summary.heroNote")}</span>
              </p>

            </section>

            <nav aria-label={t("common:nav.profile")} className="app-domain-sidebar-section">
              <p className="app-domain-sidebar-label">{t("common:nav.profile")}</p>
              <ul className="app-panel-raised mt-3 divide-y divide-[var(--app-border)] p-2">
                {[
                  ["profile-account", t("profile.identity")],
                  ["profile-preferences", t("profile.preferences")],
                  ["profile-experience", t("profile.summary.experience")],
                  ["profile-data", t(FEATURE_FLAGS.transactionCsvImport ? "profile.csvImport.open" : "profile.terms")],
                  ["profile-agents", t("profile.mcp.eyebrow")],
                ].map(([id, label]) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      className="flex min-h-14 items-center rounded-[var(--ds-radius-sm)] px-4 text-sm font-medium text-[var(--app-text)] transition hover:bg-[var(--app-surface-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--app-focus-ring)]"
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            </DomainSidebar>

            <DomainTwoColumnContent className="space-y-5">
            <SettingsSection
              id="profile-account"
              title={t("profile.summary.profileEditor")}
              description={t("profile.summary.editorHint")}
            >
              <form onSubmit={handleProfileSubmit}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <EditableField
                    id="profile-email"
                    name="email"
                    type="email"
                    label={t("profile.fields.email")}
                    value={formState.email}
                    onChange={handleProfileInputChange}
                    disabled={isSavingProfile}
                    placeholder="you@example.com"
                    className="sm:col-span-2"
                  />
                  <EditableField
                    id="profile-firstname"
                    name="firstname"
                    label={t("profile.fields.firstName")}
                    value={formState.firstname}
                    onChange={handleProfileInputChange}
                    disabled={isSavingProfile}
                    placeholder={t("profile.firstNameFallback")}
                  />
                  <EditableField
                    id="profile-lastname"
                    name="lastname"
                    label={t("profile.fields.lastName")}
                    value={formState.lastname}
                    onChange={handleProfileInputChange}
                    disabled={isSavingProfile}
                    placeholder={t("profile.lastNameFallback")}
                  />
                  <EditableField
                    id="profile-phone"
                    name="phone"
                    type="tel"
                    label={t("profile.fields.phone")}
                    value={formState.phone}
                    onChange={handleProfileInputChange}
                    disabled={isSavingProfile}
                    placeholder={t("profile.phoneFallback")}
                    className="sm:col-span-2"
                  />
                </div>

                <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-h-5">
                    {profileFeedback ? (
                      <p
                        className={`text-sm ${
                          profileFeedback.type === "success"
                            ? "text-[var(--app-success)]"
                            : "text-[var(--app-danger)]"
                        }`}
                      >
                        {profileFeedback.message}
                      </p>
                    ) : (
                      <p className="text-sm text-[var(--app-text-secondary)]">
                        {isProfileDirty ? t("profile.summary.unsavedChanges") : t("profile.summary.synced")}
                      </p>
                    )}
                  </div>

                  <AppButton
                    type="submit"
                    tone="primary"
                    disabled={isSavingProfile || !isProfileDirty || !formState.email.trim()}
                    isLoading={isSavingProfile}
                  >
                    {isSavingProfile ? t("common.states.saving") : t("profile.saveProfile")}
                  </AppButton>
                </div>
              </form>
              <button
                type="button"
                onClick={() => setIsPasswordModalOpen(true)}
                className="mt-5 flex min-h-16 w-full items-center justify-between gap-4 border-t border-[var(--app-border)] px-3 pt-3 text-left text-sm font-medium text-[var(--app-text)] transition hover:bg-[var(--app-surface-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--app-focus-ring)]"
              >
                <span className="flex items-center gap-3"><FiLock aria-hidden="true" className="h-5 w-5" />{t("profile.changePassword")}</span>
                <FiChevronRight aria-hidden="true" className="h-5 w-5 shrink-0 text-[var(--app-text-secondary)]" />
              </button>
            </SettingsSection>

            <SettingsSection id="profile-preferences" title={t("profile.summary.environment")} description={t("profile.summary.preferencesHint")}>
              <div className="space-y-6">
                <PreferenceGroup
                  label={t("common.navigation.languageMode")}
                  ariaLabel={t("common.navigation.languageMode")}
                  name="profile-language"
                  options={languageOptions}
                  activeId={language}
                  onSelect={(id) => {
                    void saveLanguage(id as "en" | "es");
                  }}
                />

                <PreferenceGroup
                  label={t("common.navigation.themeMode")}
                  ariaLabel={t("common.navigation.themeMode")}
                  name="profile-theme"
                  options={themeOptions}
                  activeId={activeThemeId}
                  onSelect={(id) => setMode(id as "light" | "dark" | "system")}
                />

              </div>
            </SettingsSection>

            <SettingsSection id="profile-experience" title={t("profile.summary.experience")} description={t("profile.summary.experienceHint")}>
              <div>
                <PreferenceGroup
                  label={t("profile.appMode.label")}
                  ariaLabel={t("profile.appMode.ariaLabel")}
                  name="profile-app-mode"
                  options={appModeOptions}
                  activeId={activeAppModeId}
                  onSelect={(id) => {
                    void saveAppMode(id as "simple" | "advanced");
                  }}
                />
              </div>
            </SettingsSection>

            <SettingsSection id="profile-data" title={t(FEATURE_FLAGS.transactionCsvImport ? "profile.csvImport.open" : "profile.terms")}>
              <div className="divide-y divide-[var(--app-border)]">
                {FEATURE_FLAGS.transactionCsvImport ? (
                  <button
                    type="button"
                    onClick={() => setIsCsvImportModalOpen(true)}
                    className="flex min-h-16 w-full items-center justify-between gap-4 rounded-[var(--ds-radius-sm)] px-3 text-left text-sm font-medium text-[var(--app-text)] transition hover:bg-[var(--app-surface-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--app-focus-ring)]"
                  >
                    <span className="flex items-center gap-3"><FiUpload aria-hidden="true" className="h-5 w-5" />{t("profile.csvImport.open")}</span>
                    <FiChevronRight aria-hidden="true" className="h-5 w-5 shrink-0 text-[var(--app-text-secondary)]" />
                  </button>
                ) : null}
                <Link to="/terms" className="flex min-h-16 items-center justify-between gap-4 rounded-[var(--ds-radius-sm)] px-3 text-sm font-medium text-[var(--app-text)] transition hover:bg-[var(--app-surface-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--app-focus-ring)]">
                  <span>{t("profile.terms")}</span>
                  <FiChevronRight aria-hidden="true" className="h-5 w-5 shrink-0 text-[var(--app-text-secondary)]" />
                </Link>
              </div>
            </SettingsSection>

            <section id="profile-agents" aria-label={t("profile.mcp.eyebrow")} className="scroll-mt-24 space-y-5">
            <McpGrantsPanel />
            <McpTokensPanel />
            </section>

            <section id="profile-danger" aria-labelledby="profile-danger-title" className="app-panel-raised scroll-mt-24 border border-[var(--app-danger)] p-5 sm:p-6">
              <p id="profile-danger-title" className="app-domain-sidebar-label text-[var(--app-danger)]">
                {t("profile.deleteAccount.sectionTitle")}
              </p>
              <p className="mt-2 text-sm leading-6 text-[var(--app-text-secondary)]">
                {t("profile.deleteAccount.sectionHint")}
              </p>
              <AppButton
                type="button"
                tone="dangerSoft"
                className="mt-4"
                onClick={() => void handleDeleteAccount()}
                disabled={isDeletingAccount || isSavingProfile}
                isLoading={isDeletingAccount}
                leadingIcon={<FiTrash2 className="h-4 w-4" />}
              >
                {isDeletingAccount ? t("common.states.deleting") : t("profile.deleteAccount.action")}
              </AppButton>
              {deleteAccountError ? (
                <p className="mt-3 text-sm text-[var(--app-danger)]" role="alert">
                  {deleteAccountError}
                </p>
              ) : null}
            </section>
            </DomainTwoColumnContent>
          </DomainTwoColumnLayout>
        </div>
      </div>
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSubmit={handlePasswordSubmit}
      />
      {FEATURE_FLAGS.transactionCsvImport ? (
        <TransactionCsvImportModal isOpen={isCsvImportModalOpen} onClose={() => setIsCsvImportModalOpen(false)} />
      ) : null}
    </main>
  );
};
